import React from "react";

// @mui material components
import Card from "@mui/material/Card";
import TextField from "@mui/material/TextField";
import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";

// Material Dashboard 2 React example components
import OrchardTimeline from "examples/Timeline/OrchardTimeline";
import BlockTimeline from "examples/Timeline/BlockTimeline";

function OrchardOverview(props) {
  const {
    currentOrchard,
    setCurrentOrchard,
    blockInfo,
    currentBlock,
    setCurrentBlock,
    orchards,
    markerMode,
    setMarkerMode,
    newMarkerLabel,
    setNewMarkerLabel,
    markerIcon,
    setMarkerIcon,
    markerTemplates,
    newMarkerTemplateTitle,
    setNewMarkerTemplateTitle,
    onSaveMarkerTemplate,
    markerTemplateError,
    orchardNotes,
    setOrchardNotes,
    blockNotes,
    setBlockNotes,
    onSaveNotes,
  } = props;

  // Predefined set of marker icons (Google Maps ready-made icons)
  const markerIcons = [
    {
      label: "Red",
      url: "https://maps.google.com/mapfiles/ms/icons/red-dot.png",
    },
    {
      label: "Green",
      url: "https://maps.google.com/mapfiles/ms/icons/green-dot.png",
    },
    {
      label: "Blue",
      url: "https://maps.google.com/mapfiles/ms/icons/blue-dot.png",
    },
    {
      label: "Yellow",
      url: "https://maps.google.com/mapfiles/ms/icons/yellow-dot.png",
    },
    {
      label: "Purple",
      url: "https://maps.google.com/mapfiles/ms/icons/purple-dot.png",
    },
    {
      label: "Orange",
      url: "https://maps.google.com/mapfiles/ms/icons/orange-dot.png",
    },
  ];

  // Handler for icon click to update selected marker icon
  const handleIconClick = (iconUrl) => {
    setMarkerIcon(iconUrl);
  };

  // Handler for keyboard events (Enter or Space)
  const handleKeyDown = (e, iconUrl) => {
    if (e.key === "Enter" || e.key === " ") {
      handleIconClick(iconUrl);
    }
  };

  return (
    <Card sx={{ height: "100%" }}>
      <MDBox pt={3} px={3}>
        <MDTypography variant="h6" fontWeight="medium">
          Orchard Selection
        </MDTypography>
      </MDBox>
      <MDBox p={2}>
        <OrchardTimeline
          color="success"
          icon="map"
          title="Orchard"
          orchardInfo={orchards}
          currentOrchard={currentOrchard}
          setCurrentBlock={setCurrentBlock}
          setCurrentOrchard={setCurrentOrchard}
        />
        {currentOrchard && (
          <BlockTimeline
            color="primary"
            icon="square"
            title="Block"
            currentBlock={currentBlock}
            setCurrentBlock={setCurrentBlock}
            blockInfo={blockInfo}
          />
        )}
      </MDBox>
      <MDBox pt={3} px={3}>
        <TextField
          label={currentBlock ? "Block Notes" : "Orchard Notes"}
          value={currentBlock ? blockNotes : orchardNotes}
          onChange={(e) =>
            currentBlock ? setBlockNotes(e.target.value) : setOrchardNotes(e.target.value)
          }
          size="small"
          variant="outlined"
          fullWidth
          multiline
          minRows={3}
          style={{ marginBottom: 10 }}
        />
        <MDButton variant="contained" color="info" size="small" onClick={onSaveNotes}>
          Save Notes
        </MDButton>

        {/* Marker label input */}
        <TextField
          label="New Marker Label"
          value={newMarkerLabel}
          onChange={(e) => setNewMarkerLabel(e.target.value)}
          size="small"
          variant="outlined"
          style={{ marginBottom: 10, display: "block" }}
        />

        {/* Marker mode checkbox */}
        <FormControlLabel
          control={
            <Checkbox checked={markerMode} onChange={(e) => setMarkerMode(e.target.checked)} />
          }
          label="Enable Marker Mode"
        />

        {/* Marker Icon Selection */}
        <div style={{ marginTop: 20 }}>
          <MDTypography variant="h6" fontWeight="medium">
            Create Marker Template
          </MDTypography>
          <TextField
            label="Marker Title"
            value={newMarkerTemplateTitle}
            onChange={(e) => setNewMarkerTemplateTitle(e.target.value)}
            size="small"
            variant="outlined"
            fullWidth
            style={{ marginTop: 10, marginBottom: 10 }}
          />
          <div style={{ display: "flex", flexWrap: "wrap", marginTop: 10 }}>
            {markerIcons.map((icon) => (
              <div
                key={icon.label}
                onClick={() => handleIconClick(icon)}
                onKeyDown={(e) => handleKeyDown(e, icon)} // Keyboard support
                tabIndex={0} // Make it focusable
                role="button" // Added role attribute for accessibility
                aria-label={`Select ${icon.label} marker`} // Optional: added aria-label for better screen reader support
                style={{
                  backgroundColor: "#f0f0f0", // Background for each icon
                  width: "40px",
                  height: "40px",
                  margin: "5px",
                  cursor: "pointer",
                  border:
                    (markerIcon?.url || markerIcon?.iconUrl) === icon.url
                      ? "2px solid black"
                      : "none", // Highlight selected icon
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <img src={icon.url} alt={icon.label} style={{ width: "100%", height: "100%" }} />
              </div>
            ))}
          </div>
          <MDButton
            variant="contained"
            color="success"
            size="small"
            onClick={onSaveMarkerTemplate}
            style={{ marginTop: 10 }}
          >
            Save Marker Template
          </MDButton>
          {markerTemplateError && (
            <MDTypography color="error" variant="caption" display="block" style={{ marginTop: 6 }}>
              {markerTemplateError}
            </MDTypography>
          )}

          <MDTypography variant="h6" fontWeight="medium" style={{ marginTop: 20 }}>
            Saved Markers
          </MDTypography>
          <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 10 }}>
            {markerTemplates.map((template) => (
              <MDButton
                key={template.id}
                variant="outlined"
                size="small"
                color="dark"
                onClick={() => setMarkerIcon(template)}
                style={{
                  justifyContent: "flex-start",
                  borderColor: markerIcon?.id === template.id ? "#000" : undefined,
                }}
              >
                <img
                  src={template.iconUrl}
                  alt={template.title}
                  style={{ width: 18, height: 18, marginRight: 8 }}
                />
                {template.title}
              </MDButton>
            ))}
          </div>
        </div>
      </MDBox>
    </Card>
  );
}

export default OrchardOverview;
